import { useEffect, useRef, useMemo } from 'react';
import type { ChatMessage } from '../../../types';

interface UseDisplayMessagesProps {
  messages: ChatMessage[];
  optimisticMessage: ChatMessage | null;
  clearOptimisticMessage: () => void;
}

/**
 * Combines real messages with the optimistic sending message and
 * clears the optimistic message once the real sent message appears.
 */
export function useDisplayMessages({
  messages,
  optimisticMessage,
  clearOptimisticMessage
}: UseDisplayMessagesProps) {
  // Clear optimistic message when the real sent message appears in the visible
  // message list (after filtering). Tracking `messages.length` instead of
  // `externalMessages.length` avoids clearing the optimistic message when
  // intermediate statuses (e.g. draft/sending) appear in the raw data but are
  // filtered out of the rendered list — which previously caused a visible gap.
  const prevMessageCountRef = useRef(messages.length);
  useEffect(() => {
    if (optimisticMessage && messages.length > prevMessageCountRef.current) {
      clearOptimisticMessage();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, optimisticMessage, clearOptimisticMessage]);

  const displayMessages = useMemo(
    () => (optimisticMessage ? [...messages, optimisticMessage] : messages),
    [messages, optimisticMessage]
  );

  return displayMessages;
}
